import { DEFINITION_LINK_PREFIX, getTextForDeprecated } from '../';
import { log } from '../../../extensionLogger';
import type { Definition } from 'typescript-json-schema';
import type { SyncRule, DeprecatedProperties, ExtensionLogger } from '@sap/ux-specification-types/dist';
import { LogSeverity, FACETTITLEPREFIX, DefinitionName } from '@sap/ux-specification-types/dist';

/**
 * Transfers the value of a single manifest setting to the config.
 *
 * @param {SyncRule} syncRule - The synchronization rule containing information about the manifest key and import handler.
 * @param {{}} manifestSection - The manifest section from which the property will be imported.
 * @param {string} propertyKey - The key of the property to be imported.
 * @param {{ [key: string]: any }} configPart - The configuration object where the property will be added.
 * @param {string} [sectionId] - Optional identifier for the section in the manifest.
 * @param {string[]} [breadcrumbs] - Optional array of path breadcrumbs for context.
 * @param {Definition} [propertyDefinition] - Optional definition object for validating the manifest value.
 */
export function importProperty(
    syncRule: SyncRule,
    manifestSection: {},
    propertyKey: string,
    configPart: { [key: string]: any },
    sectionId?: string,
    breadcrumbs?: string[],
    propertyDefinition?: Definition
): void {
    const manifestKey = syncRule.manifest.key || propertyKey;
    const importHandler = syncRule.manifest.import;
    if (importHandler && typeof importHandler === 'function') {
        const value = importHandler(manifestSection, sectionId, propertyKey, breadcrumbs);
        if (value !== undefined) {
            configPart[propertyKey] = getValidatedManifestValue(value, propertyDefinition);
        }
    } else if (manifestSection[manifestKey] !== undefined) {
        configPart[propertyKey] = getValidatedManifestValue(manifestSection[manifestKey], propertyDefinition);
    }
}

/**
 * Validation during import: check if properties are marked as deprecated.
 * If so, log them.
 *
 * @param deprecatedProperties - existing list, to be actualized
 * @param manifestSection - Section in manifest.json
 * @param path - path to the section in manifest
 * @param {ExtensionLogger} logger - Logger class for logging messages
 */
export function checkDeprecatedProperties(
    deprecatedProperties: DeprecatedProperties | undefined,
    manifestSection: {},
    path: string,
    logger?: ExtensionLogger
): void {
    if (manifestSection && deprecatedProperties) {
        for (const key in manifestSection) {
            const deprecated = deprecatedProperties[key];
            if (deprecated) {
                log(logger, {
                    severity: LogSeverity.Warning,
                    message: getTextForDeprecated(key, deprecated),
                    location: {
                        path: 'webapp/manifest.json/',
                        range: path.split('/')
                    }
                });
                delete deprecatedProperties[key];
            }
        }
    }
}

/**
 * Determines the title of a section representation in the app schema, main aspect is checking the facet ID.
 *
 * @param schemaSection - Section in manifest.json
 * @param jsonSchema - app specific schema
 * @returns {string | undefined} - title of the section
 */
export function getSectionTitle(schemaSection: Definition, jsonSchema: Definition): string | undefined {
    if (schemaSection.$ref && !schemaSection.title) {
        const refTitle = jsonSchema.definitions[schemaSection.$ref.split(DEFINITION_LINK_PREFIX)[1]] as Definition;
        return getSectionTitle(refTitle, jsonSchema);
    } else if (schemaSection.title) {
        if (schemaSection.title.startsWith(FACETTITLEPREFIX)) {
            return schemaSection.title.split(FACETTITLEPREFIX)[1].replace(/com.sap/g, '@com.sap');
        } else {
            return schemaSection.title.replace(/com.sap/g, '@com.sap');
        }
    } else {
        return undefined;
    }
}

/**
 * Method returns target by resolving custom column reference name.
 * For example - 'TableCustomColumns<dummyTarget>' is resolved as 'dummyTarget'.
 *
 * @param {string} ref Reference key.
 * @returns {string | undefined} Resolved target.
 */
export function getTargetFromCustomColumnRef(ref: string): string | undefined {
    const refParts = ref.split(`${DefinitionName.CustomColumns}<`);
    if (refParts[1]) {
        return refParts[1].split('>')[0];
    }
    return undefined;
}

/**
 * Removes part of a given config, if empty.
 *
 * @param {object} config - config part
 * @param {string} structure - part of config to be removed
 */
export function removeEmptyStructure(config: object, structure: string): void {
    if (!config[structure]) {
        return;
    }
    if (Object.keys(config[structure]).length < 1) {
        delete config[structure];
    }
}

/**
 * Validates and filters the manifest value based on the provided property definitions.
 *
 * @param {unknown | Record<string, unknown>} manifestValue - The manifest value to be validated and filtered. Can be any value or an object.
 * @param {Definition} [propertyDefinition] - The optional property definition or configuration specifying which properties to validate.
 * @returns {unknown | Record<string, unknown>} The validated and filtered manifest value.
 */
function getValidatedManifestValue(manifestValue: unknown | Record<string, unknown>, propertyDefinition?: Definition) {
    // if there are no specific property definition, we just return the manifest value
    if (!propertyDefinition) {
        return manifestValue;
    }
    // otherwise, we check the manifest value and return only the properties defined in the property definition
    const { properties } = propertyDefinition;
    return Object.keys(properties).reduce((result, currentKey) => {
        return currentKey in (manifestValue as Record<string, unknown>)
            ? { ...result, [currentKey]: manifestValue[currentKey] }
            : result;
    }, {});
}
